import {isEqual} from 'lodash';
import {useEffect, useCallback, useState, useRef, Fragment} from 'react';

import {REMOTE_MEDIA_ID, LOCAL_MEDIA_ID} from 'constants/session';
import {usePrevious} from 'hooks';
import {MediaDevicesConstraints} from 'types';
import {mediaDevicesUtils} from 'utils';

import {GroupRoomManager, PeerConnectionId, RoomEvent} from '../shared';

type Props = {
    room: GroupRoomManager;
    isAudioEnabled?: boolean;
    mediaConstraints?: MediaDevicesConstraints;
    onConnectError?: () => void;
}

export const KurentoAudio: React.FC<Props> = ({room, isAudioEnabled = true, mediaConstraints, onConnectError}) => {
    const remoteMediaRef = useRef(null);
    const localMediaRef = useRef(null);
    const roomSubscribedRef = useRef(false);
    const initialConstraints = useRef(mediaConstraints);
    const [syncStateRequired, setSyncStateRequired] = useState(true);
    const prevIsAudioEnabled = usePrevious(isAudioEnabled);

    const prevMediaConstraints = usePrevious(mediaConstraints);

    const toggleLocalAudio = useCallback(() => {
        room.localParticipant?.toggleAudioTracks?.(PeerConnectionId.Audio, isAudioEnabled);
    }, [isAudioEnabled, room]);

    const onPeerConnectionReady = useCallback(({peerId}: {peerId: string}) => {
        if (peerId === PeerConnectionId.Audio) {
            setSyncStateRequired(true);
        }
    }, []);

    const startPeerConnection = useCallback(() => {
        room.startPeerConnection(PeerConnectionId.Audio, {
            constraints: {
                audio: initialConstraints.current?.audioinput
            },
            audiooutput: initialConstraints.current?.audiooutput,
            mainRemoteParticipantElement: remoteMediaRef.current,
            localParticipantElement: localMediaRef.current
        });
    }, [room]);

    const updateAudioInputDevice = useCallback((constraints: MediaDevicesConstraints) => {
        room.localParticipant?.restartInputDevices?.(PeerConnectionId.Audio, constraints);
    }, [room.localParticipant]);

    const updateAudioOutputDevice = useCallback((deviceId: string) => {
        room.localParticipant?.restartOutputDevice?.(PeerConnectionId.Audio, deviceId);
    }, [room.localParticipant]);

    useEffect(() => {
        if (mediaConstraints &&
            !mediaDevicesUtils.isUsedInputDevicesToggled(mediaConstraints, prevMediaConstraints)) {
            if (!isEqual(mediaConstraints?.audioinput, prevMediaConstraints?.audioinput)) {
                updateAudioInputDevice(mediaConstraints);
            }
            if (!isEqual(mediaConstraints?.audiooutput, prevMediaConstraints?.audiooutput)) {
                updateAudioOutputDevice(mediaConstraints.audiooutput);
            }
        }
    }, [mediaConstraints, prevMediaConstraints, updateAudioInputDevice, updateAudioOutputDevice]);

    useEffect(() => {
        if (syncStateRequired || prevIsAudioEnabled !== isAudioEnabled) {
            toggleLocalAudio();
            if (syncStateRequired) {
                setSyncStateRequired(false);
            }
        }
    }, [toggleLocalAudio, syncStateRequired, prevIsAudioEnabled, isAudioEnabled]);

    useEffect(() => () => {
        room.stopPeerConnection(PeerConnectionId.Audio);
    }, [room]);

    useEffect(() => {
        if (!roomSubscribedRef.current) {
            const subscriptions = [
                room.on(RoomEvent.Connected, startPeerConnection),
                room.on(RoomEvent.PeerConnectionReady, onPeerConnectionReady)
            ];
            if (onConnectError) {
                subscriptions.push(room.on(RoomEvent.ConnectError, onConnectError));
            }
            roomSubscribedRef.current = true;

            if (room.isRoomStateConnected) {
                startPeerConnection();
            }

            return () => {
                subscriptions.forEach((unsubscribe) => unsubscribe());
                roomSubscribedRef.current = false;
            };
        }
    }, [room, onPeerConnectionReady, startPeerConnection, onConnectError]);

    return (
        <Fragment>
            <div ref={remoteMediaRef} id={REMOTE_MEDIA_ID} className='tw-hidden' />
            <div ref={localMediaRef} id={LOCAL_MEDIA_ID} className='tw-hidden' />
        </Fragment>
    );
};
